import { useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { api } from "../App";
import { styledToast as toast } from "@/components/ui/sonner";
import { useTranslation } from 'react-i18next';
import { useDropzone } from 'react-dropzone';
import * as XLSX from 'xlsx';
import { 
  Upload, 
  Download, 
  FileSpreadsheet, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Copy,
  Loader2,
  Users,
  AlertCircle,
  FileDown
} from "lucide-react";

export default function ImportOfficialsDialog({ open, onOpenChange, onImportComplete }) {
  const { t } = useTranslation();
  const [importing, setImporting] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [activeResultTab, setActiveResultTab] = useState("created");

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        setSelectedFile(file);
        setImportResult(null);
      } else {
        toast.error("Please upload an Excel file (.xlsx)");
      }
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    maxFiles: 1
  });

  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      const response = await api.get('/officials/bulk-import/template', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'officials_import_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(t('officialsManagement.downloadTemplateDesc'));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to download template");
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) {
      toast.error(t('officialsManagement.selectExcelFile'));
      return;
    }

    setImporting(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await api.post('/officials/bulk-import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setImportResult(response.data);
      
      if (response.data.summary.created > 0) {
        toast.success(t('officialsManagement.importSuccess'));
      } else if (response.data.summary.errors > 0) {
        toast.error(t('officialsManagement.importFailed'));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('officialsManagement.importFailed'));
    } finally {
      setImporting(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(t('officialsManagement.passwordCopied'));
  };

  const exportResults = () => {
    if (!importResult) return;

    // Create workbook
    const wb = XLSX.utils.book_new();

    // Created Officials Sheet
    if (importResult.created.length > 0) {
      const createdData = importResult.created.map((official, idx) => ({
        'Sl. No.': idx + 1,
        'Name': official.name,
        'Email': official.email,
        'Phone': String(official.phone),
        'Department': official.department,
        'Designation': official.designation,
        'Password': String(official.password || ''),
        'Taluk': official.assigned_taluk || '-',
        'Hobli': official.assigned_hobli || '-',
        'Village': official.assigned_village || '-',
        'Circle Assignments': official.circle_assignments?.map(c => {
          const circleVal = typeof c === 'string' ? c : (c?.circle || '');
          const hobliVal = typeof c === 'string' ? '' : (c?.hobli || '');
          return hobliVal ? `${hobliVal} - ${circleVal}` : circleVal;
        }).filter(Boolean).join(', ') || '-',
        'Section': official.assigned_section || '-',
        'Notes': official.notes || '-'
      }));
      
      // Create worksheet with explicit string formatting for password
      const wsCreated = XLSX.utils.json_to_sheet(createdData, { 
        cellStyles: true 
      });
      
      // Force password column to be text format
      const range = XLSX.utils.decode_range(wsCreated['!ref']);
      for (let R = range.s.r + 1; R <= range.e.r; ++R) {
        const cell_address = XLSX.utils.encode_cell({ r: R, c: 6 }); // Column G (Password)
        if (wsCreated[cell_address]) {
          wsCreated[cell_address].t = 's'; // Set type to string
          wsCreated[cell_address].z = '@'; // Text format
        }
      }
      
      // Set column widths
      wsCreated['!cols'] = [
        { wch: 8 },   // Sl. No.
        { wch: 25 },  // Name
        { wch: 30 },  // Email
        { wch: 15 },  // Phone
        { wch: 12 },  // Department
        { wch: 20 },  // Designation
        { wch: 18 },  // Password
        { wch: 12 },  // Taluk
        { wch: 15 },  // Hobli
        { wch: 18 },  // Village
        { wch: 30 },  // Circle Assignments
        { wch: 15 },  // Section
        { wch: 30 }   // Notes
      ];
      
      XLSX.utils.book_append_sheet(wb, wsCreated, 'Created Officials');
    }

    // Skipped Rows Sheet
    if (importResult.skipped.length > 0) {
      const skippedData = importResult.skipped.map((row, idx) => ({
        'Sl. No.': idx + 1,
        'Row #': row.row,
        'Name': row.name,
        'Email': row.email,
        'Reason': row.reason
      }));
      const wsSkipped = XLSX.utils.json_to_sheet(skippedData);
      wsSkipped['!cols'] = [
        { wch: 8 },
        { wch: 8 },
        { wch: 25 },
        { wch: 30 },
        { wch: 40 }
      ];
      XLSX.utils.book_append_sheet(wb, wsSkipped, 'Skipped');
    }

    // Error Rows Sheet
    if (importResult.errors.length > 0) {
      const errorData = importResult.errors.map((row, idx) => ({
        'Sl. No.': idx + 1,
        'Row #': row.row,
        'Name': row.name,
        'Email': row.email,
        'Error': row.error
      }));
      const wsErrors = XLSX.utils.json_to_sheet(errorData);
      wsErrors['!cols'] = [
        { wch: 8 },
        { wch: 8 },
        { wch: 25 },
        { wch: 30 },
        { wch: 50 }
      ];
      XLSX.utils.book_append_sheet(wb, wsErrors, 'Errors');
    }

    // Summary Sheet
    const summaryData = [
      { 'Metric': 'Total Processed', 'Count': importResult.summary.total_processed },
      { 'Metric': 'Officials Created', 'Count': importResult.summary.created },
      { 'Metric': 'Rows Skipped', 'Count': importResult.summary.skipped },
      { 'Metric': 'Rows with Errors', 'Count': importResult.summary.errors },
      { 'Metric': 'Export Date', 'Count': new Date().toLocaleString() }
    ];
    const wsSummary = XLSX.utils.json_to_sheet(summaryData);
    wsSummary['!cols'] = [{ wch: 20 }, { wch: 25 }];
    XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().slice(0, 10);
    const filename = `import_results_${timestamp}.xlsx`;

    // Download
    XLSX.writeFile(wb, filename);
    toast.success(t('officialsManagement.exportSuccess') || 'Results exported successfully');
  };

  const handleClose = () => {
    if (importResult?.summary?.created > 0) {
      onImportComplete?.();
    }
    setSelectedFile(null);
    setImportResult(null);
    onOpenChange(false);
  };

  const getDesignationLabel = (designation) => {
    const key = `designation.${designation}`;
    const translated = t(key);
    return translated !== key ? translated : designation;
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-emerald-700" />
            {t('officialsManagement.importOfficials')}
          </DialogTitle>
          <DialogDescription>
            {t('officialsManagement.importOfficialsDesc')}
          </DialogDescription>
        </DialogHeader>

        {!importResult ? (
          <div className="space-y-4 py-4">
            {/* Download Template Button */}
            <Card className="p-4 bg-blue-50 border-blue-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Download className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="font-medium text-blue-900">{t('officialsManagement.downloadTemplate')}</p>
                    <p className="text-sm text-blue-700">{t('officialsManagement.downloadTemplateDesc')}</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  onClick={handleDownloadTemplate}
                  disabled={downloadingTemplate}
                  className="border-blue-300 text-blue-700 hover:bg-blue-100"
                  data-testid="download-template-btn"
                >
                  {downloadingTemplate ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  {t('officialsManagement.downloadTemplate')}
                </Button>
              </div>
            </Card>

            {/* File Upload Dropzone */}
            <div
              {...getRootProps()}
              className={`
                border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
                ${isDragActive ? 'border-emerald-500 bg-emerald-50' : 'border-slate-300 hover:border-emerald-400 hover:bg-slate-50'}
                ${selectedFile ? 'border-emerald-500 bg-emerald-50' : ''}
              `}
              data-testid="file-dropzone"
            >
              <input {...getInputProps()} data-testid="file-input" />
              {selectedFile ? (
                <div className="space-y-2">
                  <FileSpreadsheet className="w-12 h-12 text-emerald-600 mx-auto" />
                  <p className="font-medium text-emerald-800">{selectedFile.name}</p>
                  <p className="text-sm text-emerald-600">
                    {(selectedFile.size / 1024).toFixed(1)} KB
                  </p>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFile(null);
                    }}
                    className="text-slate-500"
                  >
                    Change file
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <Upload className="w-12 h-12 text-slate-400 mx-auto" />
                  <p className="font-medium text-slate-700">
                    {isDragActive ? "Drop the file here" : t('officialsManagement.dragDropExcel')}
                  </p>
                  <p className="text-sm text-slate-500">{t('officialsManagement.supportedFormats')}</p>
                </div>
              )}
            </div>

            {/* Import Button */}
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={handleClose}>
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleImport}
                disabled={!selectedFile || importing}
                className="bg-emerald-700 hover:bg-emerald-800 text-white"
                data-testid="import-btn"
              >
                {importing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('officialsManagement.importing')}
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    {t('officialsManagement.importOfficials')}
                  </>
                )}
              </Button>
            </div>
          </div>
        ) : (
          /* Import Results */
          <div className="space-y-4 py-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-4 gap-3">
              <Card className="p-3 bg-slate-50 text-center">
                <p className="text-2xl font-bold text-slate-700">{importResult.summary.total_processed}</p>
                <p className="text-xs text-slate-500">{t('officialsManagement.totalProcessed')}</p>
              </Card>
              <Card className="p-3 bg-emerald-50 text-center">
                <p className="text-2xl font-bold text-emerald-700">{importResult.summary.created}</p>
                <p className="text-xs text-emerald-600">{t('officialsManagement.officialsCreated')}</p>
              </Card>
              <Card className="p-3 bg-amber-50 text-center">
                <p className="text-2xl font-bold text-amber-700">{importResult.summary.skipped}</p>
                <p className="text-xs text-amber-600">{t('officialsManagement.rowsSkipped')}</p>
              </Card>
              <Card className="p-3 bg-red-50 text-center">
                <p className="text-2xl font-bold text-red-700">{importResult.summary.errors}</p>
                <p className="text-xs text-red-600">{t('officialsManagement.rowsWithErrors')}</p>
              </Card>
            </div>

            {/* Detailed Results Tabs */}
            <Tabs value={activeResultTab} onValueChange={setActiveResultTab}>
              <TabsList className="w-full">
                <TabsTrigger value="created" className="flex-1 data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Created ({importResult.created.length})
                </TabsTrigger>
                <TabsTrigger value="skipped" className="flex-1 data-[state=active]:bg-amber-600 data-[state=active]:text-white">
                  <AlertTriangle className="w-4 h-4 mr-1" />
                  Skipped ({importResult.skipped.length})
                </TabsTrigger>
                <TabsTrigger value="errors" className="flex-1 data-[state=active]:bg-red-600 data-[state=active]:text-white">
                  <XCircle className="w-4 h-4 mr-1" />
                  Errors ({importResult.errors.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="created">
                <ScrollArea className="h-[300px] rounded-md border">
                  {importResult.created.length > 0 ? (
                    <div className="divide-y">
                      {importResult.created.map((official, idx) => (
                        <div key={idx} className="p-3 hover:bg-slate-50">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-slate-900">{official.name}</span>
                                <Badge variant="outline" className="text-xs">
                                  {getDesignationLabel(official.designation)}
                                </Badge>
                              </div>
                              <p className="text-sm text-slate-600">{official.email}</p>
                              <p className="text-xs text-slate-500">{official.phone}</p>
                            </div>
                            <div className="text-right">
                              <div className="flex items-center gap-2">
                                <code className="text-xs bg-slate-100 px-2 py-1 rounded font-mono">
                                  {official.password}
                                </code>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => copyToClipboard(official.password)}
                                  className="h-7 w-7 p-0"
                                  data-testid={`copy-password-${idx}`}
                                >
                                  <Copy className="w-3 h-3" />
                                </Button>
                              </div>
                              <p className="text-xs text-slate-400 mt-1">{t('officialsManagement.generatedPassword')}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-slate-400">
                      <Users className="w-12 h-12 mb-2" />
                      <p>{t('officialsManagement.noCreatedOfficials')}</p>
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>

              <TabsContent value="skipped">
                <ScrollArea className="h-[300px] rounded-md border">
                  {importResult.skipped.length > 0 ? (
                    <div className="divide-y">
                      {importResult.skipped.map((row, idx) => (
                        <div key={idx} className="p-3 hover:bg-amber-50">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs bg-amber-100">
                              {t('officialsManagement.rowNumber')}{row.row}
                            </Badge>
                            <span className="font-medium">{row.name}</span>
                          </div>
                          <p className="text-sm text-slate-600">{row.email}</p>
                          <p className="text-xs text-amber-600 mt-1">
                            <AlertTriangle className="w-3 h-3 inline mr-1" />
                            {row.reason}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-slate-400">
                      <CheckCircle className="w-12 h-12 mb-2" />
                      <p>{t('officialsManagement.noSkippedRows')}</p>
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>

              <TabsContent value="errors">
                <ScrollArea className="h-[300px] rounded-md border">
                  {importResult.errors.length > 0 ? (
                    <div className="divide-y">
                      {importResult.errors.map((row, idx) => (
                        <div key={idx} className="p-3 hover:bg-red-50">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs bg-red-100">
                              {t('officialsManagement.rowNumber')}{row.row}
                            </Badge>
                            <span className="font-medium">{row.name}</span>
                          </div>
                          <p className="text-sm text-slate-600">{row.email}</p>
                          <p className="text-xs text-red-600 mt-1">
                            <AlertCircle className="w-3 h-3 inline mr-1" />
                            {row.error}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-slate-400">
                      <CheckCircle className="w-12 h-12 mb-2" />
                      <p>{t('officialsManagement.noErrors')}</p>
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>
            </Tabs>

            {/* Action Buttons */}
            <div className="flex justify-between pt-2">
              {/* Export Results Button */}
              <Button 
                variant="outline"
                onClick={exportResults}
                className="border-blue-300 text-blue-700 hover:bg-blue-50"
                data-testid="export-results-btn"
                disabled={importResult.summary.total_processed === 0}
              >
                <FileDown className="w-4 h-4 mr-2" />
                {t('officialsManagement.exportResults')}
              </Button>
              
              {/* Close Button */}
              <Button 
                onClick={handleClose}
                className="bg-emerald-700 hover:bg-emerald-800 text-white"
                data-testid="close-results-btn"
              >
                {t('officialsManagement.closeAndRefresh')}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
